import { useEffect, useRef } from "react";

interface UsePollingOptions {
  /** Poll only while true (e.g. while a job is running). Default `true`. */
  readonly enabled?: boolean;
}

function isHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * Calls `fn` every `ms` while enabled, and pauses while the tab is hidden (no background
 * requests). When the tab becomes visible again it refreshes once right away, then resumes the
 * interval. The latest `fn` is always used, so callers need not memoise it. The first call
 * happens after `ms`; callers load their initial data themselves.
 */
export function usePolling(
  fn: () => unknown,
  ms: number,
  { enabled = true }: UsePollingOptions = {},
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || ms <= 0) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const tick = () => void fnRef.current();
    const start = () => {
      if (timer === null) timer = setInterval(tick, ms);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (isHidden()) {
        stop();
      } else if (timer === null) {
        tick();
        start();
      }
    };
    if (!isHidden()) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, ms]);
}
